import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, ChartLine, Trophy, GameController, BookOpen, Lightning, Sword, DiceFive, Club, ShieldStar, CaretRight } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';
import { useEntitlement, useSmartNudges } from '../hooks/useEntitlement';
import { LockedFeature, UpgradeChip, SmartNudgeStack } from '../components/UpgradePrompts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GAME_ICONS = {
  'Chess': Sword, 'D&D': DiceFive, 'Magic: The Gathering': Club,
  'Pokemon': Lightning, 'Yu-Gi-Oh!': Club, 'Warhammer': ShieldStar,
  'Catan': GameController,
};

const GAME_COLORS = {
  'Chess': '#00F0FF', 'D&D': '#EF4444', 'Magic: The Gathering': '#9D4CDD',
  'Pokemon': '#FACC15', 'Yu-Gi-Oh!': '#F97316', 'Warhammer': '#39FF14',
  'Catan': '#F97316',
};

export default function MultiGameStatsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [multiStats, setMultiStats] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const advancedAnalytics = useEntitlement('analytics.advanced');
  const { nudges, dismiss: dismissNudge } = useSmartNudges();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [gamesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/games`, { withCredentials: true }),
        axios.get(`${API_URL}/api/game-stats/multi/${user?.id}`, { withCredentials: true }),
      ]);
      setGames(gamesRes.data);
      setMultiStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load stats');
    } finally { setLoading(false); }
  };

  const selectGame = async (gameName) => {
    setSelectedGame(gameName);
    try {
      const { data } = await axios.get(`${API_URL}/api/games/${encodeURIComponent(gameName)}/data`, { withCredentials: true });
      setGameData(data);
    } catch { setGameData(null); }
  };

  if (loading) return <div className="app-container flex items-center justify-center min-h-screen"><div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="app-container" data-testid="multi-game-stats-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => selectedGame ? (setSelectedGame(null), setGameData(null)) : navigate(-1)}
            className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="multigame-back-button">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <ChartLine size={24} weight="duotone" className="text-[#00F0FF]" />
              {selectedGame || 'Game Hub'}
            </h1>
            <p className="text-[#71717A] text-xs">{selectedGame ? 'Rules, tutorials & stats' : 'All games & analytics'}</p>
          </div>
        </div>
      </header>

      <div className="px-6 pb-32 space-y-4">
        {!selectedGame ? (
          <>
            {/* Cross-game summary */}
            {multiStats && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-xl bg-gradient-to-br from-[#00F0FF]/10 to-[#9D4CDD]/5 border border-[#00F0FF]/20">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-white text-xl font-bold">{multiStats.total_games_all}</p>
                    <p className="text-[#71717A] text-[10px]">Total Games</p>
                  </div>
                  <div>
                    <p className="text-[#39FF14] text-xl font-bold">{multiStats.total_wins_all}</p>
                    <p className="text-[#71717A] text-[10px]">Total Wins</p>
                  </div>
                  <div>
                    <p className="text-[#FACC15] text-xl font-bold">{multiStats.overall_win_rate}%</p>
                    <p className="text-[#71717A] text-[10px]">Win Rate</p>
                  </div>
                </div>
                {multiStats.best_game && (
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-center gap-2">
                    <Trophy size={14} weight="fill" className="text-[#FACC15]" />
                    <span className="text-[#A1A1AA] text-xs">Best Game: <span className="text-white font-semibold">{multiStats.best_game}</span></span>
                  </div>
                )}
              </motion.div>
            )}

            {/* Tabs */}
            <SmartNudgeStack nudges={nudges.filter(n => n.feature_highlight?.startsWith('analytics'))} maxShow={1} onDismiss={dismissNudge} />
            <div className="flex gap-2">
              {['overview', 'analytics'].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 ${activeTab === tab ? 'bg-[#9D4CDD] text-white' : 'bg-[#181824] text-[#71717A] hover:text-white'}`}
                  data-testid={`multigame-tab-${tab}`}>
                  {tab === 'overview' ? 'All Games' : 'My Analytics'}
                  {tab === 'analytics' && !advancedAnalytics.allowed && !advancedAnalytics.loading && <UpgradeChip plan="Plus" />}
                </button>
              ))}
            </div>

            {activeTab === 'overview' && (
              <div className="space-y-2" data-testid="games-list">
                {games.map((g, i) => {
                  const Icon = GAME_ICONS[g.name] || GameController;
                  const color = GAME_COLORS[g.name] || '#9D4CDD';
                  return (
                    <motion.button key={g.name} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                      onClick={() => selectGame(g.name)}
                      className="w-full p-4 rounded-xl bg-[#0F0F16] border border-white/5 flex items-center gap-3 hover:border-white/10 transition-all text-left"
                      data-testid={`game-card-${g.name.replace(/[^a-zA-Z]/g, '')}`}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                        <Icon size={22} weight="duotone" style={{ color }} />
                      </div>
                      <div className="flex-1">
                        <p className="text-white text-sm font-semibold">{g.name}</p>
                        <p className="text-[#71717A] text-[10px]">{g.rules_count} rules · {g.tutorial_sections} tutorials · {g.setup_steps} setup steps</p>
                      </div>
                      <CaretRight size={16} className="text-[#71717A]" />
                    </motion.button>
                  );
                })}
              </div>
            )}

            {activeTab === 'analytics' && (
              <LockedFeature feature="Advanced Analytics" plan="Plus" allowed={advancedAnalytics.allowed}>
              <div className="space-y-3" data-testid="per-game-stats">
                {multiStats?.per_game?.length > 0 ? multiStats.per_game.map((s, i) => {
                  const color = GAME_COLORS[s.game_name] || '#9D4CDD';
                  return (
                    <motion.div key={s.game_name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                      className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                        <span className="text-white text-sm font-semibold">{s.game_name}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="p-2 rounded-lg bg-[#181824]">
                          <p className="text-white font-bold text-sm">{s.total_games}</p>
                          <p className="text-[#71717A] text-[10px]">Games</p>
                        </div>
                        <div className="p-2 rounded-lg bg-[#181824]">
                          <p className="text-[#39FF14] font-bold text-sm">{s.total_wins}</p>
                          <p className="text-[#71717A] text-[10px]">Wins</p>
                        </div>
                        <div className="p-2 rounded-lg bg-[#181824]">
                          <p className="text-[#FACC15] font-bold text-sm">{s.win_rate}%</p>
                          <p className="text-[#71717A] text-[10px]">Win Rate</p>
                        </div>
                      </div>
                    </motion.div>
                  );
                }) : (
                  <div className="text-center py-8">
                    <ChartLine size={40} weight="duotone" className="text-[#71717A] mx-auto mb-2" />
                    <p className="text-[#71717A] text-sm">No game stats yet. Play some games to see analytics!</p>
                  </div>
                )}

                {/* Cross-game insights */}
                {multiStats?.all_insights?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-[#A1A1AA] mb-2">Insights</h3>
                    <div className="flex flex-wrap gap-2">
                      {multiStats.all_insights.map((ins, i) => (
                        <span key={i} className="px-3 py-1.5 rounded-full bg-[#9D4CDD]/10 border border-[#9D4CDD]/20 text-[#9D4CDD] text-xs font-medium">
                          {ins.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              </LockedFeature>
            )}
          </>
        ) : (
          /* Game Detail View */
          <GameDetailView gameData={gameData} gameName={selectedGame} navigate={navigate} />
        )}
      </div>

      <BottomNav />
    </div>
  );
}

function GameDetailView({ gameData, gameName, navigate }) {
  const [tab, setTab] = useState('rules');

  if (!gameData || gameData.message) {
    if (gameName === 'Catan') {
      return (
        <div className="text-center py-8">
          <p className="text-[#A1A1AA] text-sm mb-3">Catan has dedicated stats & rules pages.</p>
          <button onClick={() => navigate('/catan-stats')} className="btn-primary text-sm" data-testid="go-catan-stats">View Catan Stats</button>
          <button onClick={() => navigate('/rules')} className="ml-2 px-4 py-2 rounded-xl bg-[#181824] text-white text-sm" data-testid="go-catan-rules">View Rules</button>
        </div>
      );
    }
    return <div className="text-center py-8"><p className="text-[#71717A] text-sm">No detailed data available for {gameName}.</p></div>;
  }

  const rules = gameData.rules || [];
  const tutorial = gameData.tutorial_sections || [];
  const setup = gameData.setup_steps || [];

  return (
    <div className="space-y-4">
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {['rules', 'tutorial', 'setup'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${tab === t ? 'bg-[#9D4CDD] text-white' : 'bg-[#181824] text-[#71717A] hover:text-white'}`}
            data-testid={`gamedetail-tab-${t}`}>
            {t === 'rules' ? `Rules (${rules.length})` : t === 'tutorial' ? `Tutorial (${tutorial.length})` : `Setup (${setup.length})`}
          </button>
        ))}
      </div>

      {tab === 'rules' && (
        <div className="space-y-2" data-testid="game-rules-list">
          {rules.map((r, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 rounded-full bg-[#9D4CDD]/10 text-[#9D4CDD] text-[10px] font-semibold">{r.category}</span>
                <h4 className="text-white text-sm font-semibold">{r.title}</h4>
              </div>
              <p className="text-[#A1A1AA] text-xs leading-relaxed">{r.simplified_text}</p>
            </motion.div>
          ))}
        </div>
      )}

      {tab === 'tutorial' && (
        <div className="space-y-2" data-testid="game-tutorial-list">
          {tutorial.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen size={16} weight="duotone" className="text-[#00F0FF]" />
                <h4 className="text-white text-sm font-semibold">{s.title}</h4>
              </div>
              <p className="text-[#A1A1AA] text-xs leading-relaxed">{s.content}</p>
            </motion.div>
          ))}
        </div>
      )}

      {tab === 'setup' && (
        <div className="space-y-2" data-testid="game-setup-list">
          {setup.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="p-4 rounded-xl bg-[#0F0F16] border border-white/5 flex gap-3">
              <div className="w-8 h-8 rounded-full bg-[#39FF14]/10 flex items-center justify-center shrink-0">
                <span className="text-[#39FF14] text-xs font-bold">{s.step}</span>
              </div>
              <div>
                <h4 className="text-white text-sm font-semibold mb-0.5">{s.title}</h4>
                <p className="text-[#A1A1AA] text-xs leading-relaxed">{s.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
